import { ApiError, classify, errorView, linkTo, moneyAction } from "@nodus/api";
import { ActionLink, Callout, ErrorState } from "@nodus/ui";
import { useRuntime } from "./runtime";
import { supportHref } from "./support";

export function isCapacityUnavailable(error: unknown): boolean {
  return error instanceof ApiError && (error.reason === "CapacityUnavailable" ||
    Boolean(error.status.details?.causes?.some((cause) => cause.reason === "CapacityUnavailable")));
}

/**
 * One rendering of API errors for forms and sections (ui-spec §4.5, §4.6): money errors route to their single fix,
 * quota errors name the quota and are never retried automatically, 403 names the missing scope, 422 lists the
 * fields, everything else is the kubectl-style error card.
 */
export function ApiErrorCallout({ error, scope, onRetry }: { error: unknown; scope: { org: string; project?: string }; onRetry?: () => void }) {
  const { config } = useRuntime();
  const c = classify(error);
  const e = error instanceof ApiError ? error : undefined;
  if (isCapacityUnavailable(error)) {
    return <Callout tone="warning" role="alert" title="This configuration is no longer available.">
      <p>Refresh availability, or choose another configuration.</p>
      {onRetry ? <button type="button" className="mt-3 font-medium underline underline-offset-4" onClick={onRetry}>Refresh availability</button> : null}
    </Callout>;
  }
  if (c === "money" && e) {
    const action = moneyAction(e, scope);
    return (
      <Callout tone="warning" role="alert" title={action?.message ?? e.message} action={action ? <ActionLink href={action.href}>{action.label}</ActionLink> : undefined}>
        {e.status.fix}
      </Callout>
    );
  }
  if (c === "quota" && e) {
    return (
      <Callout
        tone="warning"
        role="alert"
        title={e.status.message ?? "A quota is full."}
        action={<ActionLink href={`/${encodeURIComponent(scope.org)}/settings/${["node_limit", "provisioning_limit"].includes(e.reason ?? "") ? "compute" : "quotas"}`}>Manage limits</ActionLink>}
      >
        {e.status.fix}
      </Callout>
    );
  }
  if (c === "forbidden" && e) {
    return (
      <Callout tone="danger" role="alert" title={e.status.message ?? "You don't have access to this."} action={e.reason === "TrialLimitExceeded"
        ? <ActionLink href={linkTo({ kind: "BillingAccount" }, { org: scope.org })}>Add credits</ActionLink>
        : e.reason === "CloudExecuteNotEnabled" || e.reason === "ComputeSuspended"
          ? <ActionLink href={supportHref(config)} external>Contact support</ActionLink>
          : <ActionLink href={`/${encodeURIComponent(scope.org)}/team/members`}>Team</ActionLink>}>
        {e.status.fix ?? "Ask an Owner or Admin."}
      </Callout>
    );
  }
  if (c === "invalid" && e) {
    return (
      <Callout tone="danger" role="alert" title={e.status.message ?? "Some fields need attention."}>
        <ul className="mt-1 list-disc pl-5">
          {(e.status.details?.causes ?? []).map((cause, i) => (
            <li key={i}>
              {cause.field ? <code className="font-mono">{cause.field}</code> : null} {cause.message}
            </li>
          ))}
        </ul>
      </Callout>
    );
  }
  if (c === "rateLimited" && e) {
    return <Callout tone="warning" role="status" title={`Too many requests. Try again in ${e.retryAfterSeconds ?? 5} s.`} />;
  }
  return <ErrorState error={errorView(error)} {...(onRetry ? { onRetry } : {})} />;
}
